import { Icon } from '@components/primitives/Icon'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faFilter } from '@fortawesome/pro-regular-svg-icons/faFilter'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

type QuickFilterProps = {
  icon?: IconDefinition
  name?: string
  searchParam?: string
  value?: string
}

export const QuickFilter = ({
  icon = faFilter,
  name = '',
  searchParam = '',
  value = '',
}: QuickFilterProps) => {
  const searchParams = useSearchParams()
  const router = useRouter()
  const paramValue = value || name

  // Initialize local state from URL for fast UI updates
  const [isActive, setIsActive] = useState(
    searchParams?.get(searchParam) === paramValue,
  )

  // Sync with URL when search parameters change
  useEffect(() => {
    setIsActive(searchParams?.get(searchParam) === paramValue)
  }, [searchParams, searchParam, paramValue])

  const handleFilterToggle = (e: React.MouseEvent) => {
    e.preventDefault()

    // Create a new URLSearchParams instance
    const params = new URLSearchParams(searchParams?.toString() ?? '')

    // Remove the page parameter if it exists
    if (params.has('page')) {
      params.delete('page')
    }

    // Toggle the filter
    if (isActive) {
      // Remove filter if active
      params.delete(searchParam)
    } else {
      // Add filter if not active
      params.set(searchParam, paramValue)
    }

    trackEvent(TrackingEvents.SEARCH_LANDER_QUICK_FILTER_CLICKED, {
      val: name,
    })
    // Update local state immediately for responsive UI
    setIsActive(!isActive)

    // Update the URL with soft navigation
    router.push(`?${params.toString()}`, { scroll: false })
  }

  return (
    <div
      className={`group flex w-fit cursor-pointer items-center gap-2 rounded-full border-[1.5px]  px-4 py-2 transition-colors ${
        isActive
          ? 'border-primary400 bg-primary100'
          : 'border-neutral200 bg-white'
      }`}
      onClick={handleFilterToggle}
    >
      <Icon
        className={`${isActive ? 'text-primary700' : 'text-primary600'}`}
        icon={icon}
        size='sm'
      />

      <span className='whitespace-nowrap text-sm font-semibold text-neutral900 antialiased'>
        {name}
      </span>
    </div>
  )
}
